export class LanguageDetector {
  // Уникальные украинские буквы (их нет в русской раскладке)
  private ukrainianOnly = ["і", "ї", "є", "ґ", "І", "Ї", "Є", "Ґ"];

  // Уникальные русские буквы
  private russianOnly = ["ы", "э", "ъ", "Ы", "Э", "Ъ"];

  // Частотные украинские слова
  private ukrainianWords = [
    "та",
    "це",
    "що",
    "як",
    "для",
    "від",
    "про",
    "але",
    "який",
    "ця",
    "ці",
    "ті",
    "або",
    "тому",
    "після",
    "коли",
    "між",
    "під",
    "над",
    "біля",
    "також",
    "може",
    "буде",
    "були",
    "було",
    "через",
    "тільки",
    "більше",
    "дуже",
    "із",
    "зі",
    "її",
    "його",
    "їх",
    "усі",
    "всі",
    "інші",
    "інших",
  ];

  // Частотные русские слова
  private russianWords = [
    "для",
    "что",
    "как",
    "это",
    "при",
    "все",
    "или",
    "если",
    "после",
    "также",
    "более",
    "когда",
    "очень",
    "еще",
    "между",
    "только",
    "может",
    "было",
    "будет",
    "были",
    "через",
    "где",
    "эти",
    "свой",
    "который",
    "этот",
    "такой",
    "весь",
    "ещё",
    "один",
    "быть",
  ];

  /**
   * Определение языка текста
   * @param text текст для анализа
   * @returns 'ukrainian'|'russian'|'mixed'|'unknown'
   */
  detectLanguage(text: string): "ukrainian" | "russian" | "mixed" | "unknown" {
    const textLower = text.toLowerCase();

    // Проверка на кириллицу
    if (!/[а-яёіїєґ]/i.test(text)) {
      return "unknown"; // Нет кириллицы
    }

    let hasUkrainianLetters = false;
    let hasRussianLetters = false;
    let ukrainianScore = 0;
    let russianScore = 0;

    // 1. КРИТИЧЕСКИЙ КРИТЕРИЙ: Проверка уникальных букв
    // Если есть украинские буквы - это точно НЕ русский
    for (const letter of this.ukrainianOnly) {
      if (textLower.includes(letter.toLowerCase())) {
        hasUkrainianLetters = true;
        ukrainianScore += 100; // Большой вес украинским буквам
        break;
      }
    }

    // Проверка русских букв
    for (const letter of this.russianOnly) {
      if (textLower.includes(letter.toLowerCase())) {
        hasRussianLetters = true;
        russianScore += 50; // Меньший вес, так как украинцы могут писать с ошибками
        break;
      }
    }

    // 2. ПРИОРИТЕТНАЯ ЛОГИКА
    // Если есть украинские буквы и нет русских - точно украинский
    if (hasUkrainianLetters && !hasRussianLetters) {
      return "ukrainian";
    }

    // Если есть и украинские и русские буквы - смешанный
    if (hasUkrainianLetters && hasRussianLetters) {
      return "mixed";
    }

    // 3. Если нет уникальных букв - анализируем слова
    const words = textLower.split(/[\s\-_]+/);

    for (const word of words) {
      // Пропускаем латиницу и цифры
      if (/^[a-z0-9]+$/i.test(word)) {
        continue;
      }

      if (this.ukrainianWords.includes(word)) {
        ukrainianScore += 20;
      }
      if (this.russianWords.includes(word)) {
        russianScore += 20;
      }
    }

    // 4. Анализ окончаний
    // Украинские окончания
    if (/(ння|ття|ючи|ючись|ється|ються|ській|ській)$/.test(textLower)) {
      ukrainianScore += 15;
    }

    // Русские окончания
    if (/(ние|тие|ущий|ющий|ется|ются|ыми|ными|ский)$/.test(textLower)) {
      russianScore += 15;
    }

    // 5. Финальное решение
    // Если есть только русские буквы
    if (hasRussianLetters && !hasUkrainianLetters) {
      // Но если украинских слов больше - это украинский (русская раскладка по ошибке)
      if (ukrainianScore > russianScore * 1.5) {
        return "ukrainian";
      }
      return "russian";
    }

    // Если нет уникальных букв - определяем по словам
    if (ukrainianScore === 0 && russianScore === 0) {
      // Проверяем есть ли вообще кириллица
      if (/[а-яё]/i.test(text)) {
        return "mixed"; // Кириллица есть, но язык не определен
      }
      return "unknown";
    }

    // Приоритет украинскому при близких значениях
    if (ukrainianScore >= russianScore * 0.8) {
      return "ukrainian";
    }

    return "russian";
  }

  /**
   * Проверка соответствия языковому фильтру
   * @param text текст для проверки
   * @param operator оператор фильтра
   * @returns соответствует ли текст фильтру
   */
  matchesLanguageFilter(text: string, operator: string): boolean {
    const textLower = text.toLowerCase();

    // Проверка на кириллицу (хотя бы одна кириллическая буква)
    if (!/[а-яёіїєґ]/i.test(text)) {
      return false; // Нет кириллицы - не попадает ни в один языковой фильтр
    }

    // Проверка украинских признаков
    let hasUkrainianSigns = false;
    let hasRussianSigns = false;

    // 1. Украинские буквы - явный признак
    for (const letter of this.ukrainianOnly) {
      if (textLower.includes(letter.toLowerCase())) {
        hasUkrainianSigns = true;
        break;
      }
    }

    // 2. Русские буквы - явный признак
    for (const letter of this.russianOnly) {
      if (textLower.includes(letter.toLowerCase())) {
        hasRussianSigns = true;
        break;
      }
    }

    // Если есть явные буквенные признаки - возвращаем результат
    if (hasUkrainianSigns || hasRussianSigns) {
      switch (operator) {
        case "is_ukrainian":
          return hasUkrainianSigns; // Приоритет украинскому
        case "is_russian":
          return hasRussianSigns && !hasUkrainianSigns;
        default:
          return false;
      }
    }

    // 3. Если нет явных букв - проверяем слова
    const words = textLower
      .split(/[\s\-_]+/)
      .filter((w) => /[а-яёіїєґ]/i.test(w));

    let ukrainianWordCount = 0;
    let russianWordCount = 0;

    for (const word of words) {
      if (this.ukrainianWords.includes(word)) {
        ukrainianWordCount++;
      }
      if (this.russianWords.includes(word)) {
        russianWordCount++;
      }
    }

    // 4. Проверяем окончания
    if (/(ння|ття|ючи|ючись|ється|ються|ській)$/.test(textLower)) {
      ukrainianWordCount++;
    }
    if (/(ние|тие|ущий|ющий|ется|ются|ыми|ными|ский)$/.test(textLower)) {
      russianWordCount++;
    }

    // Если есть признаки только одного языка
    if (ukrainianWordCount > 0 && russianWordCount === 0) {
      hasUkrainianSigns = true;
    } else if (russianWordCount > 0 && ukrainianWordCount === 0) {
      hasRussianSigns = true;
    }
    // Если нет признаков или есть признаки обоих языков - не определен

    switch (operator) {
      case "is_ukrainian":
        return hasUkrainianSigns;

      case "is_russian":
        return hasRussianSigns && !hasUkrainianSigns;

      default:
        return false;
    }
  }
}

// Экспортируем единственный экземпляр
export const languageDetector = new LanguageDetector();
